// FacilityTable.tsx
import React from 'react';
import { Table, Tag, Image, Button, Tooltip, Space } from 'antd';
import { DownOutlined, UpOutlined, EnvironmentOutlined, PhoneOutlined, MailOutlined, ClockCircleOutlined, EyeOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { EditOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './Table.css';

interface FacilityTableProps {
  data: any;
  loading?: boolean;
  onEdit: any;
}

const DataTable: React.FC<FacilityTableProps> = ({ data, loading, onEdit }) => {
  const navigate = useNavigate();

  // Navigate to facility details page
  const handleViewDetails = (facilityId: string) => {
    navigate(`/facilities/${facilityId}`);
  };
  
  // Facility type color mapping
  const getFacilityTypeColor = (type: string): string => {
    const colorMap: Record<string, string> = {
      'hospital': '#f5222d',
      'pharmacy': '#52c41a',
      'clinic': '#1890ff',
      'laboratory': '#722ed1',
      'imaging': '#fa8c16',
      'dental': '#13c2c2'
    };
    
    return colorMap[type.toLowerCase()] || '#2db7f5';
  };
  
  // Format day of week to be more readable
  const formatDay = (day: string): string => {
    return day.charAt(0).toUpperCase() + day.slice(1);
  };
  
  // Expanded row render function
  const expandedRowRender = (record: any) => {
    return (
      <div className="expanded-row">
        <div className="expanded-row-content">
          {/* Contact Information Section */}
          <div className="expanded-section">
            <h4><PhoneOutlined /> Contact Information</h4>
            <div className="contact-info">
              <p><strong>Primary:</strong> {record.phone1}</p>
              {record.phone2 && <p><strong>Secondary:</strong> {record.phone2}</p>}
              <p><strong>Email:</strong> {record.email}</p>
            </div>
          </div>

          {/* Services Section */}
          <div className="expanded-section">
            <h4><ClockCircleOutlined /> Services</h4>
            <div className="services-list">
              {record.services.map((service: string, index: number) => (
                <Tag key={index} color="blue">{service}</Tag>
              ))}
            </div>
          </div>

          {/* Opening Hours Section */}
          <div className="expanded-section">
            <h4><ClockCircleOutlined /> Opening Hours</h4>
            <div className="hours-grid">
              {Object.entries(record.openingHours).map(([day, hours]: [string, any]) => (
                <div key={day} className="hours-item">
                  <span className="day-label">{formatDay(day)}</span>
                  <span className="hours-value">{hours}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Facility Tags Section */}
          <div className="expanded-section">
            <h4><ClockCircleOutlined /> Facility Tags</h4>
            <div className="tags-list">
              {record.facilityTags.map((tag: string, index: number) => (
                <Tag key={index} color="green">{tag}</Tag>
              ))}
            </div>
          </div>

          {/* Location Details Section */}
          <div className="expanded-section">
            <h4><EnvironmentOutlined /> Location Details</h4>
            <p><strong>Coordinates:</strong> {record.coordinates.latitude}, {record.coordinates.longitude}</p>
          </div>
        </div>
      </div>
    );
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Facility',
      dataIndex: 'facilityName',
      key: 'facilityName',
      sorter: (a, b) => a.facilityName.localeCompare(b.facilityName),
      render: (text: string, record: any) => (
        <div className="facility-name-cell">
          <div className="facility-icon-container">
            <Image
              width={40}
              height={40}
              src={record.facilityIcon}
              alt={text}
              className="facility-icon"
              fallback="data:image/png;base64,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"
            />
          </div>
          <div className="facility-details">
            <div className="facility-name">{text}</div>
            <Tag color={getFacilityTypeColor(record.facilityType)}>
              {record.facilityType.charAt(0).toUpperCase() + record.facilityType.slice(1)}
            </Tag>
          </div>
        </div>
      )
    },
    {
      title: 'Address',
      key: 'address',
      render: (text: string, record: any) => (
        <div className="address-cell">
          <EnvironmentOutlined className="address-icon" />
          <div className="address-text">
            <div className="address-street">{record.address.street}</div>
            <div className="address-detail">
              {record.address.area}, {record.address.city}
            </div>
          </div>
        </div>
      )
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (text: string, record: any) => (
        <div className="contact-cell">
          <div className="phone-number">
            <PhoneOutlined className="contact-icon" /> {record.phone1}
          </div>
          <div className="email-text">
            <MailOutlined className="contact-icon" /> {record.email}
          </div>
        </div>
      )
    },
    {
      title: 'Primary Services',
      key: 'services',
      render: (text: string, record: any) => (
        <div className="services-cell">
          {record.services.slice(0, 2).map((service: string, index: number) => (
            <Tag key={index} color="blue">{service}</Tag>
          ))}
          {record.services.length > 2 && (
            <Tooltip title={record.services.slice(2).join(', ')}>
              <Tag className="more-tag">+{record.services.length - 2} more</Tag>
            </Tooltip>
          )}
        </div>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (text: string, record: any) => (
        <Space size="small">
          <Button 
            type="primary" 
            icon={<EditOutlined />}
            onClick={() => onEdit(record)}
            className="edit-button"
            style={{ backgroundColor: '#322659', borderColor: '#322659' }}
          >
            Edit
          </Button>
          <Button
            type="default"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record.id)}
            className="view-details-button"
          >
            Details
          </Button>
        </Space>
      ),
    }
  ];

  return (
    <Table
      className="facility-table"
      columns={columns}
      dataSource={data}
      loading={loading}
      expandable={{
        expandedRowRender,
        expandIcon: ({ expanded, onExpand, record }) => (
          <Button 
            className={`expand-button ${expanded ? 'expanded' : ''}`}
            onClick={e => onExpand(record, e)}
            type="text"
            icon={expanded ? <UpOutlined className="expanded-icon" /> : <DownOutlined />}
          >
            {expanded ? 'Hide Details' : 'View Details'}
          </Button>
        )
      }}
      rowKey="id"
      pagination={{
        pageSize: 10,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50'],
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} facilities`
      }}
    />
  );
};
  
export default DataTable;